import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import InputBase from "@mui/material/InputBase";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import YelloBrand from "src/assets/Images/yellow-brand.png";
import useAuth from "src/hooks/useAuth";
import { isEmail, isPhone } from "src/utils/common";
import { PATH_AUTH } from "src/routes/paths";
import PasswordInputBase from "src/components/PasswordInputBase";
import ProfileSwitcher from "./ProfileSwitcher";

export default function Signin() {
  const [loading, setLoading] = useState(false);
  const [emailOrPhone, setEmailOrPhone] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const { login, profileId, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    let values = {};
    if (isEmail(emailOrPhone)) {
      values.email = emailOrPhone;
    } else if (isPhone(emailOrPhone)) {
      values.userName = emailOrPhone;
    } else {
      setError("Please input valid email or phone");
    }
    if (values.email || values.userName) {
      setLoading(true);
      try {
        values.password = password;
        await login(values);
      } catch (error) {
        setLoading(false);
        if ("message" in error.data) {
          setError(error.data.message);
        }
      }
    }
  };

  if (!isAuthenticated) {
    return (
      <Stack spacing={2} p={{ xs: 2, md: 5 }} sx={{ bgcolor: "common.black" }}>
        <Stack justifyContent="center" alignItems="center">
          <Box
            component="img"
            src={YelloBrand}
            alt="yellow_brand_logo"
            onClick={() => navigate("/")}
            sx={{ width: 85, height: 75, cursor: "pointer" }}
          />
          <Typography variant="h4" color="white" textAlign="center">
            Login to AFRIKAST
          </Typography>
        </Stack>
        <Stack
          spacing={3}
          component="form"
          autoComplete="off"
          onSubmit={onSubmit}
          alignItems="center"
        >
          {error !== "" && (
            <Alert
              severity="error"
              onClose={() => {
                setError("");
              }}
              sx={{ maxWidth: { sx: 300, xs: 275 } }}
            >
              {error}
            </Alert>
          )}

          <InputBase
            fullWidth
            required
            sx={{
              p: 1,
              padding: 1.5,
              borderRadius: 1,
              fontSize: { xs: 12, sm: 14 },
              color: "text.primary",
              bgcolor: "grey.900",
            }}
            value={emailOrPhone}
            disabled={loading}
            onChange={(evt) => {
              if (error) {
                setError("");
              }
              setEmailOrPhone(evt.target.value);
            }}
            placeholder="Email or Mobile Phone"
          />
          <PasswordInputBase
            sx={{
              p: 1,
              padding: 1.5,
              borderRadius: 1,
              fontSize: { xs: 12, sm: 14 },
              color: "text.primary",
              bgcolor: "grey.900",
            }}
            value={password}
            disabled={loading}
            placeholder="Password"
            onChange={(evt) => {
              if (error) {
                setError("");
              }
              setPassword(evt.target.value);
            }}
          />

          <Button
            size="large"
            type="submit"
            variant="contained"
            disabled={loading}
            color="inherit"
            endIcon={
              !loading ? (
                <ArrowForwardIosIcon />
              ) : (
                <CircularProgress size={20} sx={{ color: "text.secondary" }} />
              )
            }
          >
            Next
          </Button>
        </Stack>
        <FormControlLabel
          control={<Checkbox color="primary" />}
          label={
            <Typography
              color="white"
              variant="subtitle1"
              sx={{ fontStyle: "italic" }}
            >
              Remember me
            </Typography>
          }
        />
        <Stack px={1} spacing={1}>
          <Typography
            component={Link}
            to={PATH_AUTH.forgotPassword}
            variant="subtitle1"
            sx={{
              fontWeight: 500,
              color: "text.primary",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Forgot password?
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="subtitle1"
              sx={{ fontStyle: "italic", fontWeight: 500 }}
            >
              New to AFRICAST?
            </Typography>
            <Typography
              component={Link}
              to={PATH_AUTH.register}
              variant="subtitle1"
              sx={{
                color: "text.primary",
                textDecoration: "none",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              Subscribe here
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    );
  } else if (!profileId && isAuthenticated) {
    return <ProfileSwitcher />;
  }
}
